/**
 * PartnerPayoutSection — /partner/onboarding (Phase I payout funnel).
 *
 * Renders the 7-state Stripe-Connect-Express payout onboarding UI:
 *
 *   payout_pending         → "Start payout setup" CTA
 *   payout_link_issued     → "Continue at Stripe" + "I finished" (refresh)
 *   payout_in_review       → informational (Stripe reviewing)
 *   payout_charges_enabled → "You can accept bookings now" (payouts still pending)
 *   payout_payouts_enabled → celebration + link to /dashboard/provider
 *   payout_disabled        → friendly requirements + "Fix payout issue" CTA
 *   payout_link_expired    → "Resume payout setup" (regenerate-link)
 *
 * Copy discipline (Family H): partner-facing text uses only the
 * approved verification claims ("Identity-verified via Stripe" /
 * "Manually reviewed by our team") plus payout-specific plain
 * language. Requirements from Stripe are ALWAYS translated to friendly
 * labels server-side (see backend `friendly_requirements()`); unknown
 * fields collapse to a single generic line.
 *
 * Mounts under PartnerOnboardingPage AFTER verification.status ===
 * "approved" — the parent gates rendering so the payout section
 * never appears for a partner who hasn't cleared identity yet.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  ExternalLink,
  RefreshCw,
  ArrowRight,
  CheckCircle2,
  Clock,
  AlertTriangle,
  DollarSign,
  Wallet,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const PAYOUT_LABEL = {
  payout_pending: "Ready for payout setup",
  payout_link_issued: "Payout setup in progress",
  payout_in_review: "Stripe reviewing payout setup",
  payout_charges_enabled: "You can accept bookings now",
  payout_payouts_enabled: "You're a live PooPartner",
  payout_disabled: "Stripe needs more info",
  payout_link_expired: "Payout setup link expired",
};

/* ------------------------------------------------------------------ */
function PayoutStatusBadge({ status }) {
  const map = {
    payout_pending: { color: "#6b7280", bg: "#f3f4f6" },
    payout_link_issued: { color: "#7c3aed", bg: "#f5f3ff" },
    payout_in_review: { color: "#b45309", bg: "#fffbeb" },
    payout_charges_enabled: { color: "#0369a1", bg: "#eff6ff" },
    payout_payouts_enabled: { color: "#065f46", bg: "#d1fae5" },
    payout_disabled: { color: "#991b1b", bg: "#fee2e2" },
    payout_link_expired: { color: "#b45309", bg: "#fffbeb" },
  };
  const p = map[status] || map.payout_pending;
  return (
    <span
      data-testid={`partner-payout-status-${status}`}
      style={{
        display: "inline-block",
        padding: "4px 10px",
        borderRadius: 999,
        color: p.color,
        background: p.bg,
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {PAYOUT_LABEL[status] || status}
    </span>
  );
}

function StepCard({ testId, icon, title, body, cta }) {
  return (
    <Card
      data-testid={testId}
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <CardHeader>
        <CardTitle
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 18,
            fontWeight: 700,
            color: "#374751",
          }}
        >
          <span style={{ color: "#F89623" }}>{icon}</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent style={{ color: "#4b5563", lineHeight: 1.6 }}>
        {body}
        {cta && <div style={{ marginTop: 16 }}>{cta}</div>}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
export default function PartnerPayoutSection() {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "start" | "refresh" | "regen"

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/partner/payout/me");
      setRow(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const startPayout = async () => {
    setBusy("start");
    try {
      const { data } = await api.post("/partner/payout/start");
      setRow(data.row);
      toast.success("Payout setup started. Complete on Stripe, then come back.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const refreshPayout = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post("/partner/payout/refresh");
      setRow(data);
      if (data.status === "payout_payouts_enabled") {
        toast.success("Payouts are live!");
      } else if (data.status === "payout_disabled") {
        toast.error("Stripe needs more info from you.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const regenLink = async () => {
    setBusy("regen");
    try {
      const { data } = await api.post("/partner/payout/regenerate-link");
      setRow(data.row);
      toast.success("Fresh setup link issued.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div
        style={{ padding: 20, textAlign: "center" }}
        data-testid="partner-payout-loading"
      >
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }
  if (!row) return null;

  return (
    <div
      style={{ marginTop: 28 }}
      data-testid="partner-payout-section"
    >
      <header style={{ marginBottom: 16 }}>
        <span
          style={{
            color: "#F89623",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: 1.5,
            textTransform: "uppercase",
          }}
          data-testid="partner-payout-eyebrow"
        >
          Payout setup
        </span>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 800,
            margin: "6px 0 10px 0",
            color: "#374751",
          }}
          data-testid="partner-payout-heading"
        >
          Connect your payouts
        </h2>
        <PayoutStatusBadge status={row.status} />
      </header>

      {row.status === "payout_pending" && (
        <StepCard
          testId="partner-payout-step-pending"
          icon={<Wallet size={22} />}
          title="Set up payouts with Stripe"
          body={
            <p>
              You're verified! Next: set up payouts so we can send you your
              money for every booking you fulfill. Stripe handles this on
              their side — we never see your bank details.
            </p>
          }
          cta={
            <Button
              onClick={startPayout}
              disabled={busy === "start"}
              data-testid="partner-payout-start-btn"
            >
              {busy === "start" ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  Start payout setup
                  <ArrowRight size={16} style={{ marginLeft: 8 }} />
                </>
              )}
            </Button>
          }
        />
      )}

      {row.status === "payout_link_issued" && (
        <StepCard
          testId="partner-payout-step-link-issued"
          icon={<Loader2 size={22} />}
          title="Finish on Stripe"
          body={
            <div>
              <p style={{ marginBottom: 12 }}>
                Complete your payout details on Stripe's secure page. Once
                you're done, come back and hit Refresh so we pick up the
                result.
              </p>
              {row.provider_link_url && (
                <a
                  href={row.provider_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 18px",
                    background: "#F89623",
                    // AA-normal on brand-orange: 5.31:1 (was 4.30:1, below 4.5:1).
                    // --pp-cta-text is theme-agnostic (identical value in both
                    // theme blocks), matching the existing brand-invariant hex
                    // on the surrounding line.
                    color: "var(--pp-cta-text)",
                    borderRadius: 999,
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                  data-testid="partner-payout-hosted-url"
                >
                  Continue at Stripe
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          }
          cta={
            <Button
              variant="outline"
              onClick={refreshPayout}
              disabled={busy === "refresh"}
              data-testid="partner-payout-refresh-btn"
            >
              {busy === "refresh" ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <RefreshCw size={16} style={{ marginRight: 8 }} />
                  I finished on Stripe
                </>
              )}
            </Button>
          }
        />
      )}

      {row.status === "payout_in_review" && (
        <StepCard
          testId="partner-payout-step-in-review"
          icon={<Clock size={22} />}
          title="Stripe is reviewing your payout setup"
          body={
            <p>
              Nothing to do here — Stripe usually finishes this in 1-2
              business days. You'll get an email once it's done.
            </p>
          }
          cta={
            <Button
              variant="outline"
              onClick={refreshPayout}
              disabled={busy === "refresh"}
              data-testid="partner-payout-refresh-btn"
            >
              {busy === "refresh" ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <RefreshCw size={16} style={{ marginRight: 8 }} />
                  Check status
                </>
              )}
            </Button>
          }
        />
      )}

      {row.status === "payout_charges_enabled" && (
        <StepCard
          testId="partner-payout-step-charges-enabled"
          icon={<CheckCircle2 size={22} color="#0369a1" />}
          title="You can accept bookings now"
          body={
            <p>
              Great news — Stripe has cleared you to accept bookings.
              Payouts will start once Stripe finishes their final review
              (usually a business day or two). You can start plucking
              today; the money will start flowing shortly.
            </p>
          }
          cta={
            <Button
              variant="outline"
              onClick={refreshPayout}
              disabled={busy === "refresh"}
              data-testid="partner-payout-refresh-btn"
            >
              {busy === "refresh" ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <RefreshCw size={16} style={{ marginRight: 8 }} />
                  Refresh status
                </>
              )}
            </Button>
          }
        />
      )}

      {row.status === "payout_payouts_enabled" && (
        <StepCard
          testId="partner-payout-step-payouts-enabled"
          icon={<DollarSign size={22} color="#065f46" />}
          title="All set — you're a live PooPartner"
          body={
            <p>
              Identity-verified via Stripe, manually reviewed by our team,
              and now payouts are live. Every booking you fulfill will pay
              out on Stripe's standard schedule. Time to make some magic
              happen for your neighbors' yards.
            </p>
          }
          cta={
            <Link
              to="/dashboard/provider"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "10px 18px",
                background: "#F89623",
                // AA-normal on brand-orange: 5.31:1 (was 4.30:1, below 4.5:1).
                // --pp-cta-text is theme-agnostic (identical value in both
                // theme blocks), matching the existing brand-invariant hex
                // on the surrounding line.
                color: "var(--pp-cta-text)",
                borderRadius: 999,
                textDecoration: "none",
                fontWeight: 700,
              }}
              data-testid="partner-payout-continue-btn"
            >
              Go to dashboard <ArrowRight size={16} />
            </Link>
          }
        />
      )}

      {row.status === "payout_disabled" && (
        <StepCard
          testId="partner-payout-step-disabled"
          icon={<AlertTriangle size={22} color="#991b1b" />}
          title="Stripe needs more info from you"
          body={
            <div>
              <p style={{ marginBottom: 12 }}>
                Stripe paused your payouts pending some additional info. Fix
                the items below and payouts will resume automatically once
                Stripe re-verifies.
              </p>
              {row.friendly_requirements &&
                row.friendly_requirements.length > 0 && (
                <ul
                  style={{
                    margin: "12px 0",
                    paddingLeft: 20,
                    color: "#374751",
                    lineHeight: 1.7,
                  }}
                  data-testid="partner-payout-requirements-list"
                >
                  {row.friendly_requirements.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              )}
            </div>
          }
          cta={
            <Button
              onClick={regenLink}
              disabled={busy === "regen"}
              data-testid="partner-payout-fix-btn"
            >
              {busy === "regen" ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                "Fix payout issue"
              )}
            </Button>
          }
        />
      )}

      {row.status === "payout_link_expired" && (
        <StepCard
          testId="partner-payout-step-link-expired"
          icon={<AlertTriangle size={22} color="#b45309" />}
          title="Your setup link expired"
          body={
            <p>
              No problem — Stripe expires setup links after a short window.
              Grab a fresh one below and finish where you left off.
            </p>
          }
          cta={
            <Button
              onClick={regenLink}
              disabled={busy === "regen"}
              data-testid="partner-payout-resume-btn"
            >
              {busy === "regen" ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                "Resume payout setup"
              )}
            </Button>
          }
        />
      )}
    </div>
  );
}
